import { Alert, Box, Button, Paper, Typography } from "@mui/material";

import type { AuthUser } from "@/HelloCashflowDomain/Authentication/types/auth.types";

type AdminAccessDeniedPanelProps = {
    isLoggingOut: boolean;
    onLogout: () => void;
    user: AuthUser;
};

const AdminAccessDeniedPanel = ({
    isLoggingOut,
    onLogout,
    user,
}: AdminAccessDeniedPanelProps) => {
    return (
        <Box
            sx={{
                alignItems: "center",
                bgcolor: "background.default",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: "100vh",
                p: 3,
                textAlign: "center",
            }}>
            <Paper
                elevation={0}
                sx={{
                    borderColor: "divider",
                    borderRadius: 2,
                    borderStyle: "solid",
                    borderWidth: 1,
                    maxWidth: 560,
                    p: 4,
                    width: "100%",
                }}>
                <Typography variant='h4' sx={{ fontWeight: 700 }}>
                    Access Denied
                </Typography>
                <Typography sx={{ mt: 1 }} color='text.secondary'>
                    You don&apos;t have admin permissions to access this page.
                </Typography>
                <Alert severity='info' sx={{ mt: 3, textAlign: "left" }}>
                    Please ask a developer for admin access (include the Firebase
                    user UID below).
                </Alert>

                <Box sx={{ mt: 3, textAlign: "left" }}>
                    <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
                        Signed in as
                    </Typography>
                    <Typography variant='body2'>
                        <Box component='span' sx={{ fontWeight: 600 }}>
                            Name:
                        </Box>{" "}
                        {user.displayName || "(not set)"}
                    </Typography>
                    <Typography variant='body2'>
                        <Box component='span' sx={{ fontWeight: 600 }}>
                            Email:
                        </Box>{" "}
                        {user.email || "(not available)"}
                    </Typography>
                    <Typography variant='body2' sx={{ mt: 1 }} color='text.secondary'>
                        Not your usual admin account? Log out and sign in again with your
                        admin account.
                    </Typography>
                    <Button
                        variant='contained'
                        color='error'
                        sx={{ mt: 2 }}
                        disabled={isLoggingOut}
                        onClick={onLogout}>
                        Logout
                    </Button>
                </Box>

                <Box sx={{ mt: 3, textAlign: "left" }}>
                    <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
                        Firebase user UID
                    </Typography>
                    <Box
                        component='code'
                        sx={{
                            bgcolor: "action.hover",
                            borderRadius: 1,
                            display: "block",
                            fontFamily: "monospace",
                            fontSize: 12,
                            p: 1.5,
                            userSelect: "all",
                            wordBreak: "break-all",
                        }}>
                        {user.uid}
                    </Box>
                    <Button
                        variant='outlined'
                        size='small'
                        sx={{ mt: 2 }}
                        onClick={() => {
                            void navigator.clipboard
                                ?.writeText(user.uid)
                                .catch(() => undefined);
                        }}>
                        Copy UID
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default AdminAccessDeniedPanel;

